import { addDoc, collection, doc, getDoc } from 'firebase/firestore'
import { db } from '@/firebase/init'
import router from '@/router'

const typeConfig = {
  story: {
    collection: 'stories',
    routeName: 'RealmStory',
    name: 'Story'
  },
  idea: {
    collection: 'ideas',
    routeName: 'RealmIdea',
    name: 'Idea'
  },
  lesson: {
    collection: 'lessons',
    routeName: 'RealmLesson',
    name: 'Lesson'
  },
  game: {
    collection: 'games',
    routeName: 'RealmGame',
    name: 'Game'
  },
  conundrum: {
    collection: 'conundrums',
    routeName: 'RealmConundrum',
    name: 'Conundrum'
  }
};

export const creatorService = {

  /**
   * Create a new item (story, idea, lesson, game) and navigate to its editor.
   * @param {string} type - 'story', 'idea', 'lesson', or 'game'
   * @param {object} profile - The user's profile object.
   */
  async create(type, profile) {
    const config = typeConfig[type];
    if (!config) {
      console.error(`Invalid type "${type}" provided to creatorService.create`);
      return { success: false, error: `Invalid type: ${type}` };
    }

    const newItem = {
      name: `Untitled ${config.name}`,
      description: `A new ${type} by ${profile.name}`,
      creator: {
        id: profile.id,
      },
      state: 'draft',
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    
    if (type === 'story') {
        newItem.settings = {};
        newItem.files = [];
    } else if (type === 'game') {
        newItem.codebase = {
          code: '',
          complete: '',
          language: 'javascript',
          original: {},
          dependencies: ['p5.js'],
          versions: []
        };
        newItem.settings = {};
        newItem.thumbnail = "";
        newItem.files = [];
    } else if (type === 'conundrum') {
        newItem.title = 'Untitled Conundrum';
        newItem.prompt = '';
        newItem.options = [
          { id: 'opt1', text: 'Option A' },
          { id: 'opt2', text: 'Option B' }
        ];
    }

    try {
      const collectionRef = collection(db, config.collection);
      const docRef = await addDoc(collectionRef, newItem);

      router.push({ name: config.routeName, params: { id: docRef.id } });

      return { success: true, id: docRef.id };
    } catch (error) {
      console.error(`Error creating ${type}:`, error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Navigate to the editor for an existing item.
   * @param {string} type - 'story', 'idea', 'lesson', or 'game'
   * @param {string} itemId - The ID of the item to edit.
   */
  edit(type, itemId) {
    const config = typeConfig[type];
    if (!config) {
      console.error(`Invalid type "${type}" provided to creatorService.edit`);
      return;
    }
    router.push({ name: config.routeName, params: { id: itemId } });
  },

  /**
   * Create a "riff" (copy) of an existing item.
   * @param {string} type - 'story', 'idea', 'lesson', or 'game'
   * @param {string} originalId - The ID of the item to riff from.
   * @param {object} profile - The user's profile object.
   */
  async riff(type, originalId, profile) {
    const config = typeConfig[type];
    if (!config) {
      console.error(`Invalid type "${type}" provided to creatorService.riff`);
      return { success: false, error: `Invalid type: ${type}` };
    }

    try {
      const originalRef = doc(db, config.collection, originalId);
      const originalSnap = await getDoc(originalRef);

      if (!originalSnap.exists()) {
        throw new Error(`Original ${type} not found`);
      }

      const originalItem = originalSnap.data();

      const riffedItem = {
        ...originalItem,
        name: `Riff of ${originalItem.name || `Untitled ${config.name}`}`,
        description: `A riff by ${profile.name} based on "${originalItem.name || `Untitled ${config.name}`}"`,
        creator: {
          id: profile.id,
        },
        originalId: originalId, // Reference to the original
        state: 'draft',
        createdAt: new Date(),
        updatedAt: new Date(),
      };
      
      delete riffedItem.id;

      const collectionRef = collection(db, config.collection);
      const docRef = await addDoc(collectionRef, riffedItem);

      router.push({ name: config.routeName, params: { id: docRef.id } });

      return { success: true, id: docRef.id };
    } catch (error) {
      console.error(`Error creating ${type} riff:`, error);
      return { success: false, error: error.message };
    }
  }
}; 